import { apiFetch } from "../api"
import type {
    OrderingPropertiesForMarketResponse,
    OrderingPropertyMeta,
    OrderingPropertyType,
    OrderingPropertyValueHint,
} from "./orderingPropertyTypes"

const PROPERTY_TYPES: OrderingPropertyType[] = [
    "string",
    "integer",
    "decimal",
    "boolean",
    "date",
    "unknown",
]

function normalizePropertyType(raw: unknown): OrderingPropertyType {
    const s = String(raw ?? "").trim().toLowerCase()
    return (PROPERTY_TYPES.includes(s as OrderingPropertyType) ? s : "unknown") as OrderingPropertyType
}

function normalizeValueHint(raw: unknown): OrderingPropertyValueHint | undefined {
    if (!raw || typeof raw !== "object") return undefined
    const o = raw as Record<string, unknown>
    const kind = String(o.kind ?? "").trim()

    if (kind === "enum_samples" && Array.isArray(o.samples)) {
        const samples = o.samples
            .map((v) => String(v ?? "").trim())
            .filter(Boolean)
            .slice(0, 8)
        if (samples.length) return { kind: "enum_samples", samples }
        return undefined
    }

    if (kind === "numeric_range") {
        const min = Number(o.min)
        const max = Number(o.max)
        if (Number.isFinite(min) && Number.isFinite(max)) {
            return { kind: "numeric_range", min, max }
        }
        return undefined
    }

    if (kind === "boolean" && Array.isArray(o.values)) {
        const values = o.values.filter((v): v is boolean => typeof v === "boolean")
        if (values.length) return { kind: "boolean", values }
        return undefined
    }

    if (Array.isArray(o.values) && o.values.every((v) => typeof v === "boolean")) {
        return { kind: "boolean", values: o.values as boolean[] }
    }

    return undefined
}

function normalizePropertyMeta(raw: unknown): OrderingPropertyMeta | null {
    if (typeof raw === "string") {
        const name = raw.trim()
        return name ? { name, type: "unknown" } : null
    }
    if (!raw || typeof raw !== "object") return null
    const o = raw as Record<string, unknown>
    const name = String(o.name ?? o.original ?? "").trim()
    if (!name) return null
    const label = String(o.label ?? "").trim()
    return {
        name,
        type: normalizePropertyType(o.type),
        ...(label ? { label } : {}),
        value_hint: normalizeValueHint(o.value_hint ?? o.valueHint),
    }
}

function normalizePropertyList(raw: unknown): OrderingPropertyMeta[] {
    if (!Array.isArray(raw)) return []
    return raw.map(normalizePropertyMeta).filter((p): p is OrderingPropertyMeta => p !== null)
}

export function normalizeOrderingPropertiesResponse(raw: unknown): OrderingPropertiesForMarketResponse {
    if (!raw || typeof raw !== "object") {
        return { market: "", ordering: [], filtering: [] }
    }
    const o = raw as Record<string, unknown>
    let data: Record<string, unknown> = o
    if (o.data != null && typeof o.data === "object") {
        data = o.data as Record<string, unknown>
    }

    const market = String(data.market ?? data.idMarket ?? "").trim()
    const ordering = normalizePropertyList(data.ordering)
    const filtering = normalizePropertyList(data.filtering)
    const legacy = normalizePropertyList(data.properties)

    if (ordering.length === 0 && filtering.length === 0 && legacy.length > 0) {
        return { market, ordering: legacy, filtering: [] }
    }

    return { market, ordering, filtering }
}

/**
 * GET /chat/get_ordering_properties_for_market?market={idMarket}
 */
export const getOrderingPropertiesForMarket = async (
    market: string
): Promise<OrderingPropertiesForMarketResponse> => {
    const m = String(market ?? "").trim()
    if (!m) return { market: "", ordering: [], filtering: [] }
    const qs = `?market=${encodeURIComponent(m)}`
    const raw = await apiFetch(`/chat/get_ordering_properties_for_market${qs}`)
    const normalized = normalizeOrderingPropertiesResponse(raw)
    return {
        market: normalized.market || m,
        ordering: normalized.ordering,
        filtering: normalized.filtering,
    }
}
